import React, { useState, useEffect, useRef, memo } from 'react';
import Dialog from '../../GenericComponents/dialog';
import { useFetchObjectMutation, useFetchAppSecurityCodesMutation, useCreatePdfMutation } from '../../Redux/OCV/OCVSlice';
import { createAndDownloadPdf, saveExcel } from './OCVUtils';
import { useOCVDataProcessor } from './useOCVDataProcessor';
import { OCVTable } from './OCVTable';
import { OCVDialogProps } from './OCVTypes';
import { set } from 'react-hook-form';


const OCVDialog: React.FC<OCVDialogProps> = memo(({
  clickThruElement,
  objectContext,
  launcherMode,
  ocvType,
  stackTraceMessage = 'Error',
  onClose,
  onWidthChange,
  onLoadingChange
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fetchObject] = useFetchObjectMutation();
  const [fetchAppSecurityCodes, { error: appSecurityError }] = useFetchAppSecurityCodesMutation();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<JSX.Element | null>(null);
  const [exportOptions, setExportOptions] = useState<JSX.Element | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  //const [tableObjects, setTableObjects] = useState<any[]>([]); 
  const { processedData, processObjectData } = useOCVDataProcessor();
  const contentRef = useRef<HTMLDivElement>(null);
  const [createPdf] = useCreatePdfMutation();
  // const [isPdfExcelLoading, setIsPdfExcelLoading] = useState(false);
  const [displayDownloadBtn,setDisplayDownloadBtn] = useState(false);
  const [Title, setTitle] = useState("");
  useEffect(() => {
    let mounted = true;

    const initDialog = async () => {
      if (mounted) {
        var titleKey = "";
        if (ocvType != "GenericOCV") {
           titleKey = " (" + clickThruElement?.innerText + ")";
          }
     setTitle(objectContext.title + titleKey || "Generic ObjectViewer");
      
        await openObjectViewDialog();
      }
        
      }
    

    initDialog();

    return () => {
      mounted = false;
    };
  }, [objectContext.key, objectContext.mdsColumnID]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Monitor content width changes
  useEffect(() => {
    if (!contentRef.current || !onWidthChange) return;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const tables = entry.target.getElementsByClassName('vu-table');
        let maxWidth = 1300;

        Array.from(tables).forEach(table => {
          const { width } = table.getBoundingClientRect();
          maxWidth = Math.max(maxWidth, width);
        });

        onWidthChange(maxWidth);
      }
    });

    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, [onWidthChange]);


  const openObjectViewDialog = async () => {
    let key = objectContext.key;
    let titleKey = " ";
    const securityCodeFetched = await fetchAppSecurityCodes().unwrap();
      if(securityCodeFetched?.includes('AllowDataExport')) {
        setDisplayDownloadBtn(true);
      }
    if (ocvType !== "GenericOCV") {
      titleKey = ocvType !== "" ? ` (${clickThruElement?.innerText})` : " ";
    }

    if (ocvType !== "GenericOCV" && !key) {
      throw new Error("Cannot retrieve object without key");
    }

    let mdsColumnID = -1;
    if (ocvType !== "GenericOCV" && ocvType !== "SmartOCV" && ocvType !== "StackTraceOCV") {
      mdsColumnID = objectContext.mdsColumnID || -1;
      if (!mdsColumnID) {
        const cell = clickThruElement?.closest("td") || clickThruElement?.closest("span");
        mdsColumnID = Number(cell?.getAttribute("MDSColumnId")) || -1;
      }
      if (!mdsColumnID) {
        throw new Error("Cannot retrieve object without MDSColumnDetails");
      }
    }

    const title = objectContext.title || "ObjectViewer";
    const reportname = title.split(' ').join('_');

    if (ocvType !== "GenericOCV" && ocvType !== "SmartOCV" && ocvType !== "StackTraceOCV") {
      if (typeof key === "string" && !key.endsWith("|str")) {
        key += "|str";
      }
    }

    if (ocvType === "GenericOCV" || launcherMode === "GenericFlat" || launcherMode === "flat") {
      key = objectContext.key;
    }

    if (launcherMode === "flat" || launcherMode === "GenericFlat") {
      setIsDialogOpen(false); // Don't show dialog
      setLoading(true);
      try {
        await fetchObjectData(mdsColumnID, key, "", ocvType);
      } catch (error) {
        console.error('Failed to fetch object data:', error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // For non-flat modes, show dialog
    setIsDialogOpen(true);
    setLoading(true);

    if (ocvType === "StackTraceOCV") {
      setContent(<div><p style={{ color: 'red' }}>{stackTraceMessage}</p></div>);
      setLoading(false);
    } else {
      try {
        await fetchObjectData(mdsColumnID, key, reportname, ocvType);
      } catch (error) {
        console.error('Failed to fetch object data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchObjectData = async (mdsColumnID: number, key: any, reportname: string, ocvType: string) => {
    let fetchUrl = "Object/Generic";
    let method = "POST";
    let contentType = "application/json; charset=utf-8";
    let requestObject: any = { mdsColumnID, key };
    let data = JSON.stringify(requestObject);
    let permaLink = `${window.location.origin}/ux/index.html#OCV/DefaultView`;

    switch (ocvType) {
      case "SmartOCV":
        fetchUrl = "SmartObject";
        method = "POST";
        contentType = "application/json; json";
        requestObject = { ciphertxt: key };
        data = JSON.stringify(requestObject);
        permaLink = `${window.location.origin}/ux/index.html#OCV/DefaultView/SmartOCV?ciphertxt=${key}`;
        break;
      case "GenericOCV":
        for (let i = 0; i < objectContext.Links.POST.length; i++) {
          if (objectContext.Links.POST[i].Rel === "Object") {
            fetchUrl = objectContext.Links.POST[i].Url;
            method = "POST";
            contentType = "application/json; charset=utf-8"
            data = objectContext.Links.POST[i].Template;
            permaLink = `${window.location.origin}/ux/index.html#OCV/GenericView/${objectContext.Id}/${JSON.parse(objectContext.Links.POST[i].Template).key}`;
          }
        }
        break;
      default:
        if (objectContext.LauncherMode === "GenericFlat") {
          fetchUrl = `Object/${objectContext.Id}`;
          method = "POST";
          contentType = "application/json; charset=utf-8"
          data = JSON.stringify({ key: objectContext.key });
          permaLink = `${window.location.origin}/ux/index.html#OCV/GenericView/${objectContext.Id}/${objectContext.key}`;
        }
        else if (objectContext.LauncherMode === "flat") {
          fetchUrl = "Object/Generic";
          method = "POST";
          contentType = "application/json; charset=utf-8"
          requestObject = { mdsColumnID, key: objectContext.key };
          data = JSON.stringify(requestObject);
          permaLink = `${window.location.origin}/ux/index.html#OCV/DefaultView/${mdsColumnID}/${objectContext.key}`;
        }
        else {
          fetchUrl = "Object/Generic";
          permaLink = `${window.location.origin}/ux/index.html#OCV/DefaultView/${mdsColumnID}/${key}`;
          method = "POST";
          contentType = "application/json; charset=utf-8";
          requestObject = {
            mdsColumnID: mdsColumnID,
            key: key
          };
          data = JSON.stringify(requestObject);
        }
    }

    try {
      const response = await fetchObject({ url: fetchUrl, method, contentType, data }).unwrap();
      if (response && response.data) {
        const objectData = (ocvType === "SmartOCV" || ocvType === "GenericOCV" || objectContext.LauncherMode === "GenericFlat") ? response.data.Objects : response.data;
        renderObjectData(objectData, reportname, ocvType, permaLink);
      }
      else {
        setContent(
          <div
        dangerouslySetInnerHTML={{
          __html: `
            <div class='padding-top-medium'>${response.statusMessage || ''}</div>
            <div class='padding-top-medium'>${response.hints || ''}</div>
          `
        }}
      />
        );
      }
    } catch (response:any) {
      setContent(
        <div
        dangerouslySetInnerHTML={{
          __html: `
            <div class='padding-top-medium'>${response.data?.statusMessage || ''}</div>
            <div class='padding-top-medium'>${response.data?.hints || ''}</div>
          `
        }}
      />
      );
    }
  };

  const renderObjectData = (objectData: any, reportname: string, ocvType: string, permaLink: string,) => {
    const processed = processObjectData(objectData, ocvType, launcherMode);

    if (!processed) {
      setContent(<div className='padding-top-medium'>Requested report does not exist.</div>);
      return;
    }

    // Fetch app security codes when needed
    fetchAppSecurityCodes().then((response) => {
      const appSecurityCodes = response.data;
      if (appSecurityCodes?.includes('AllowDataExport')) {
        setDisplayDownloadBtn(true);
        const exportOptionsBuilder = (
          ocvType !== "GenericOCV" || (ocvType === "GenericOCV" && !objectContext.Name?.includes("Report"))
          ? (
            <div className='export-dd-content'>
              <a
                href='javascript:void(0)'
                className={loading ? 'disabled' : ''}
                onClick={async (e) => {
                  e.preventDefault();
                  if (!loading) {
                    await createAndDownloadPdf(createPdf, permaLink, reportname, setLoading);
                  }
                }}
              >
                Export as PDF
              </a>
              <a href='javascript:void(0)'
                onClick={(e) => {
                  e.preventDefault();
                  saveExcel(reportname, processed.exportData, e);
                }}>
                Export as EXCEL
              </a>
            </div>
          )
          : (
            <div className='export-dd-content' style={{ top: '-45px' }}>
              <a href='javascript:void(0)'
                onClick={(e) => {
                  e.preventDefault();
                  saveExcel(reportname, processed.exportData, e);
                }}>
                Export as EXCEL
              </a>
            </div>
          )
        );
        setExportOptions(exportOptionsBuilder);
        setShowExportOptions(true);
      }
    }).catch((error) => {
      console.error('Failed to fetch app security codes:', error);
    });
    // Render content
    setContent(
      <div className={`ocv-content ${launcherMode === "GenericFlat" || launcherMode === "flat"
        ? 'ocv-flat-mode' : ''}`}>
        {processed.fixed.length > 0 && (
          <div className="ocv_fixed_section">
            {processed.fixed.map((table, index) => (
              <OCVTable
                key={`fixed-${index}`}
                tableKey={table.key}
                data={table}
                metadata={table.metadata || {}}
                isFixedSection={true}
                baseUrl={window.location.origin}
                launcherMode = {launcherMode}

              />
            ))}
          </div>
        )}
        {processed.moving.length > 0 && (
          <div className="ocv_moving_div">
            {processed.moving.map((table, index) => (
              <React.Fragment key={`moving-${index}`}>
                {table.needsSeparator && (
                  <hr id="ocv_fixed_moving_separator" />
                )}
                <OCVTable
                  tableKey={table.key}
                  data={table}
                  metadata={table.metadata || {}}
                  isFixedSection={false}
                  baseUrl={window.location.origin}
                  launcherMode = {launcherMode}

                />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );


  };

  if (launcherMode === "flat" || launcherMode === "GenericFlat") {
    return (
      <div
        id="generic_object_viewer_dialog_wrapper"
        className="hr_comment_dialog_wrapper_flat"
        style={{ marginLeft: "1%", marginRight: "1%", width: '100%' }}
      >
        {content}
      </div>
    );
  }

  return (
    <Dialog
      isOpen={isDialogOpen}
      title={Title}
      onClose={onClose}
      width="90%"
      height="80%"
      ButtonDivClasses="ocv-dialog-buttons"
      loading={loading}
      loadingText="Loading..."
      buttons={[
        ...(displayDownloadBtn ? [{
          label: "Download",
          id: 'btnDownload',
          onClick: () => {

          },
          className: "app-button float-right",
          CustomDivs: showExportOptions && exportOptions
        }] : []),
        {
          label: "Close",
          onClick: onClose,
          className: "app-button float-right",
        }
      ]}
    >
      <>
        <div ref={contentRef} style={{ overflowY: 'auto', maxHeight: 'calc(100% - 50px)' }}>
          {content}
        </div>
      </>
    </Dialog>
  );
});

export default OCVDialog;